import { Dialog, Transition } from "@headlessui/react";
import { FC, Fragment, MutableRefObject, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { noop } from "@silo/utils";
import { CloseIcon, SiloLogoIcon } from "../../icons";

/**
 * TODO: fix padding-right issue when there is a scrollbar
 *
 * https://github.com/tailwindlabs/headlessui/issues/839
 */

export interface ModalProps {
  isOpen: boolean;
  closeModal: () => void;
  variant?: ModalVariant;
  notDismissable?: boolean;
  initialFocus?: MutableRefObject<HTMLElement | null>;
  children?: ReactNode;
}

type ModalVariant = "default" | "default-wide" | "transparent";

const panelClassMap: Record<ModalVariant, string> = {
  default: "bg-shale-900 sm:bg-shale-750 shadow-xl",
  ["default-wide"]: "bg-shale-900 sm:bg-shale-750 shadow-xl sm:max-w-2xl",
  transparent: "",
};

export const Modal = ({
  isOpen,
  closeModal: closeModalProp,
  variant = "default",
  notDismissable = false,
  initialFocus,
  children,
}: ModalProps): JSX.Element => {
  const dismissable = !notDismissable;
  const closeModal = dismissable ? closeModalProp : noop;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={closeModal}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-shale-900/30 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-0 text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  "@sm:relative flex @sm:block flex-col justify-between w-screen h-screen px-5 pt-5 pb-10 overflow-auto text-left text-white align-middle transition-all transform sm:w-full sm:max-w-lg sm:h-auto sm:pb-5 sm:rounded-lg",
                  panelClassMap[variant]
                )}
              >
                <div className="flex justify-between sm:hidden">
                  <div>
                    {variant !== "transparent" && (
                      <SiloLogoIcon className="h-9" />
                    )}
                  </div>

                  {dismissable && <CloseButton closeModal={closeModal} />}
                </div>

                {/* TODO: The X close button is absolutely positioned, meaning that it can overlap with the modal content. This needs to be fixed at some point */}
                {dismissable && (
                  <CloseButton
                    className="absolute hidden sm:block right-5 top-5"
                    closeModal={closeModal}
                  />
                )}

                <div>{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

interface CloseButtonProps {
  closeModal: () => void;
  className?: string;
}

const CloseButton: FC<CloseButtonProps> = ({ className, closeModal }) => {
  return (
    <button
      className={twMerge(className)}
      type="button"
      onClick={closeModal}
      aria-label="Close modal"
    >
      <CloseIcon className="w-7 h-7" />
    </button>
  );
};

export const useModalState = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return { isModalOpen, setIsModalOpen, openModal, closeModal };
};
